import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Shared back-navigation control for drill-in views (list → detail,
 * list → create form). Renders a direction-aware back arrow + label so
 * every drilled-in screen has a consistent way back.
 *
 * Navigation precedence: `onClick` (e.g. a parent-supplied onBack/onCancel)
 * → `to` (explicit parent path) → `navigate(-1)` (browser history). Prefer
 * `to`/`onClick` over history so a deep-linked page still goes somewhere
 * sensible instead of leaving the app.
 *
 * The arrow SVG points inline-start; it's mirrored under RTL (`:root[dir=rtl]`)
 * so it points the correct way in Hebrew. Color is inherited (currentColor),
 * so it adapts to dark hero headers via a passed `className`/`style`.
 */
export default function BackButton({
  to,
  onClick,
  label,
  className,
  style,
}: {
  to?: string;
  onClick?: () => void;
  label?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const text = label ?? t('actions.back');
  const handle = onClick ?? (() => (to ? navigate(to) : navigate(-1)));
  return (
    <button
      type="button"
      className={`btn-back${className ? ` ${className}` : ''}`}
      onClick={handle}
      aria-label={text}
      style={style}
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
      </svg>
      <span>{text}</span>
    </button>
  );
}
